import axios from 'axios';
import moment from 'moment';
import { Mixpanel } from '../utils/mixpanel';

import { concat, filter, uniq } from 'lodash';

import { gatherErrors, checkIfErrorCodePresent } from '../utils/errorHandling';

import { ORGANISATIONS_API_CLIENT } from '../middleware/multiClientHandler';

import urls from '../constants/urls';

import { REQUEST_INVITE_SUCCESS } from './inviteRequests';

export const CURRENT_USER_SET = 'currentUser/CURRENT_USER_SET';

export const RELOAD_FAILED = 'currentUser/RELOAD_FAILED';
export const RELOAD_SUCCESS = 'currentUser/RELOAD_SUCCESS';
export const RELOADING_CURRENT_USER = 'currentUser/RELOADING_CURRENT_USER';

export const SIGNIN_FAILED = 'currentUser/SIGNIN_FAILED';
export const UNCONFIRMED_USER_SIGNIN = 'currentUser/UNCONFIRMED_USER_SIGNIN';
export const SIGNIN_SUCCESS = 'currentUser/SIGNIN_SUCCESS';
export const SIGNING_IN = 'currentUser/SIGNING_IN';

export const REFRESH_SESSION_TOKEN_SUCCESS =
  'currentUser/REFRESH_SESSION_TOKEN_SUCCESS';
export const REFRESH_SESSION_TOKEN_FAILED =
  'currentUser/REFRESH_SESSION_TOKEN_FAILED';
export const REFRESHING_SESSION_TOKEN = 'currentUser/REFRESHING_SESSION_TOKEN';
export const STORE_REFRESHING_PROMISE = 'currentUser/STORE_REFRESHING_PROMISE';

export const SIGNOUT = 'currentUser/SIGNOUT';

export const INVALID_CURRENT_PASSWORD = 'currentUser/INVALID_CURRENT_PASSWORD';
export const UPDATE_USER_FAILED = 'currentUser/UPDATE_USER_FAILED';
export const UPDATE_USER_SUCCESS = 'currentUser/UPDATE_USER_SUCCESS';
export const UPDATING_USER = 'currentUser/UPDATING_USER';
export const USER_EMAIL_CHANGE_REQUESTED =
  'currentUser/USER_EMAIL_CHANGE_REQUESTED';

export const FAVOURITE_HOSPITAL_FAILED =
  'currentUser/FAVOURITE_HOSPITAL_FAILED';
export const FAVOURITE_HOSPITAL_SUCCESS =
  'currentUser/FAVOURITE_HOSPITAL_SUCCESS';
export const FAVOURITING_HOSPITAL = 'currentUser/FAVOURITING_HOSPITAL';

export const UNFAVOURITE_HOSPITAL_FAILED =
  'currentUser/UNFAVOURITE_HOSPITAL_FAILED';
export const UNFAVOURITE_HOSPITAL_SUCCESS =
  'currentUser/UNFAVOURITE_HOSPITAL_SUCCESS';
export const UNFAVOURITING_HOSPITAL = 'currentUser/UNFAVOURITING_HOSPITAL';

export const DEFAULT_HOSPITAL_FAILED = 'currentUser/DEFAULT_HOSPITAL_FAILED';
export const DEFAULT_HOSPITAL_SUCCESS = 'currentUser/DEFAULT_HOSPITAL_SUCCESS';
export const DEFAULTING_HOSPITAL = 'currentUser/DEFAULTING_HOSPITAL';

export const HARD_DELETE_FAILED = 'currentUser/HARD_DELETE_FAILED';
export const HARD_DELETE_SUCCESS = 'currentUser/HARD_DELETE_SUCCESS';
export const HARD_DELETING = 'currentUser/HARD_DELETING';

export const FORBIDDEN_REQUEST = 'currentUser/FORBIDDEN_REQUEST';

export const UPDATING_GDPR_CONSENT = 'currentUser/UPDATING_GDPR_CONSENT';
export const UPDATE_GDPR_SUCCESS = 'currentUser/UPDATE_GDPR_SUCCESS';
export const UPDATE_GDPR_FAILED = 'currentUser/UPDATE_GDPR_FAILED';

export const UPDATING_USER_TEALIUM_ID = 'currentUser/UPDATING_USER_TEALIUM_ID';
export const UPDATE_USER_TEALIUM_ID_SUCCESS =
  'currentUser/UPDATE_USER_TEALIUM_ID_SUCCESS';
export const UPDATE_USER_TEALIUM_ID_FAILED =
  'currentUser/UPDATE_USER_TEALIUM_ID_FAILED';

export const ADMINED_ORGS_FAILED = 'currentUser/ADMINED_ORGS_FAILED';
export const ADMINED_ORGS_SUCCESS = 'currentUser/ADMINED_ORGS_SUCCESS';
export const ADMINED_ORGS = 'currentUser/ADMINED_ORGS';

export const clearSession = () => {
  localStorage.removeItem('currentUser.user_id');
  localStorage.removeItem('currentUser.sessionToken');
  localStorage.removeItem('currentUser.refreshToken');
  localStorage.removeItem('currentUser.tokenExpiration');
  localStorage.removeItem('currentUser.hasSeenUnsupportedEmailModal');
};

const saveSessionDetails = (
  userId,
  sessionToken,
  refreshToken,
  tokenExpiration
) => {
  localStorage.setItem('currentUser.user_id', userId);
  localStorage.setItem('currentUser.sessionToken', sessionToken);
  localStorage.setItem('currentUser.refreshToken', refreshToken);
  localStorage.setItem('currentUser.tokenExpiration', tokenExpiration.toJSON());
  if (sessionToken) {
    axios.defaults.headers.common.Authorization = `Token token=${sessionToken}`;
  }
};

const baseInitialState = {
  adminedHospitals: [],
  adminedOrganisations: [],
  defaultHospital: null,
  favouritedHospitals: [],
  initialLoad: true,
  rank: null,
  email: null,
  sessionToken: null,
  user: null,

  updateErrors: {},

  failedReload: false,
  failedSaving: false,
  failedSettingDefault: false,
  failedSignIn: false,
  failedSignOut: false,
  failedTokenRefresh: false,

  isRefreshingToken: false,
  isSaving: false,
  isSettingDefault: false,
  isSigningIn: false,
  isSigningOut: false,

  isRefreshingTokenPromise: null,

  favouritingHospitalIds: [],
  unfavouritingHospitalIds: [],
};

const initialState = {
  ...baseInitialState,
  sessionToken: localStorage.getItem('currentUser.sessionToken'),
  refreshToken: localStorage.getItem('currentUser.refreshToken'),
  tokenExpiration: moment(localStorage.getItem('currentUser.tokenExpiration')),
};

const handleSignInSuccess = (state, action) => {
  const tokenExpiration = moment().add(moment.duration(30, 'minutes'));

  saveSessionDetails(
    action.payload.data.id,
    action.payload.data.token,
    action.payload.data.refresh_token,
    tokenExpiration
  );

  Mixpanel.identify(action.payload.data.mixpanel_id);

  return {
    ...state,
    sessionToken: action.payload.data.token,
    refreshToken: action.payload.data.refresh_token,
    tokenExpiration,
    user: action.payload.data,
    rank: action.payload.data.rank,
    email: action.payload.data.email,
    defaultHospital: action.payload.data.hospital,
    favouritedHospitals: action.payload.data.favourited_hospitals,
    adminedHospitals: action.payload.data.admined_hospitals,
    failedHardDeleting: false,
    failedSignIn: false,
    initialLoad: false,
    isHardDeleting: false,
    isSigningIn: false,
  };
};

export default (state = initialState, action) => {
  switch (action.type) {
    case SIGNING_IN:
      return {
        ...state,
        isSigningIn: true,
        failedSignIn: false,
        failedReload: false,
      };

    case SIGNIN_SUCCESS:
      return handleSignInSuccess(state, action);

    case UNCONFIRMED_USER_SIGNIN:
    case SIGNIN_FAILED:
      return {
        ...state,
        isSigningIn: false,
        failedSignIn: true,
      };

    case SIGNOUT:
      return baseInitialState;

    case REFRESHING_SESSION_TOKEN:
      return {
        ...state,
        isRefreshingToken: true,
        failedTokenRefresh: false,
      };

    case STORE_REFRESHING_PROMISE:
      return {
        ...state,
        isRefreshingTokenPromise: action.promise,
      };

    case REFRESH_SESSION_TOKEN_SUCCESS:
      return {
        ...handleSignInSuccess(state, action),
        failedTokenRefresh: false,
        initialLoad: false,
        isRefreshingToken: false,
        isRefreshingTokenPromise: null,
      };

    case REFRESH_SESSION_TOKEN_FAILED:
      return {
        ...state,
        failedTokenRefresh: true,
        isRefreshingToken: false,
        isRefreshingTokenPromise: null,
        rank: null,
        user: null,
      };

    case RELOADING_CURRENT_USER:
      return {
        ...state,
        isReloading: true,
        reloadFailed: false,
      };

    case RELOAD_SUCCESS:
      return {
        ...state,
        user: action.payload.data,
        rank: action.payload.data.rank,
        defaultHospital: action.payload.data.hospital,
        favouritedHospitals: action.payload.data.favourited_hospitals,
        adminedHospitals: action.payload.data.admined_hospitals,
        isSigningIn: false,
      };

    case RELOAD_FAILED:
      return {
        ...state,
        isReloading: false,
        reloadFailed: true,
      };

    case UPDATING_USER:
      return {
        ...state,
        isSaving: true,
        failedSaving: false,
        updateErrors: {},
      };

    case UPDATE_USER_SUCCESS:
      return {
        ...state,
        user: action.payload.data,
        rank: action.payload.data.rank,
        defaultHospital: action.payload.data.hospital,
        favouritedHospitals: action.payload.data.favourited_hospitals,
        adminedHospitals: action.payload.data.admined_hospitals,
        isSaving: false,
        updateErrors: {},
      };

    case UPDATE_USER_FAILED:
      return {
        ...state,
        isSaving: false,
        failedSaving: true,
        updateErrors: gatherErrors(action.error, 'update_user_failed'),
      };

    case FAVOURITING_HOSPITAL:
      const concatedFavouritingHospitalIds = uniq(
        concat(state.favouritingHospitalIds, action.favouritingHospitalId)
      );

      return {
        ...state,
        favouritingHospitalIds: concatedFavouritingHospitalIds,
        favouritingHospitalFailed: false,
      };

    case FAVOURITE_HOSPITAL_SUCCESS:
      return {
        ...state,
        favouritedHospitals: uniq(
          concat(state.favouritedHospitals, action.meta.previousAction.hospital)
        ),
      };

    case FAVOURITE_HOSPITAL_FAILED:
      const filteredFavouritingHospitalIds = filter(
        state.favouritingHospitalIds,
        action.meta.previousAction.favouritingHospitalId
      );

      return {
        ...state,
        favouritingHospitalIds: filteredFavouritingHospitalIds,
        favouritingHospitalFailed: true,
      };

    case UNFAVOURITING_HOSPITAL:
      const concatedUnfavouritingHospitalIds = uniq(
        concat(state.unfavouritingHospitals, action.unfavouritingHospitalId)
      );

      return {
        ...state,
        unfavouritingHospitals: concatedUnfavouritingHospitalIds,
        unfavouritingHospitalFailed: false,
      };

    case UNFAVOURITE_HOSPITAL_SUCCESS:
      let unfavouritedHospitalId =
        action.meta.previousAction.unfavouritedHospitalId;

      const newFavouritedHospitals = filter(
        state.favouritedHospitals,
        hospital => {
          return hospital.id !== unfavouritedHospitalId;
        }
      );

      return {
        ...state,
        favouritedHospitals: uniq(newFavouritedHospitals),
      };

    case UNFAVOURITE_HOSPITAL_FAILED:
      const filteredUnfavouritingHospitalIds = filter(
        state.unfavouritingHospitals,
        action.meta.previousAction.unfavouritingHospitalId
      );

      return {
        ...state,
        unfavouritingHospitals: filteredUnfavouritingHospitalIds,
        unfavouritingHospitalFailed: true,
      };

    case DEFAULTING_HOSPITAL:
      return {
        ...state,
        isSettingDefault: true,
      };

    case DEFAULT_HOSPITAL_SUCCESS:
      return {
        ...state,
        defaultHospital: action.payload.data.hospital,
        isSettingDefault: false,
      };

    case DEFAULT_HOSPITAL_FAILED:
      return {
        ...state,
        isSettingDefault: false,
        failedSettingDefault: true,
      };

    // Handle a succesful invite request creation from the inviteRequests reducer
    // If the request was for the default hospital then we should mark it as invite_requested
    case REQUEST_INVITE_SUCCESS:
      let defaultHospital = state.defaultHospital;
      let requestedHospitalId = action.meta.previousAction.hospitalId;

      if (defaultHospital && requestedHospitalId === defaultHospital.id) {
        return {
          ...state,
          defaultHospital: {
            ...state.defaultHospital,
            invite_requested: true,
          },
        };
      }
      return state;

    case HARD_DELETING:
      return {
        ...state,
        isHardDeleting: true,
        failedHardDeleting: false,
      };

    case HARD_DELETE_SUCCESS:
      return {
        ...state,
        isHardDeleting: false,
      };

    case HARD_DELETE_FAILED:
      return {
        ...state,
        isHardDeleting: false,
        failedHardDeleting: true,
      };

    case ADMINED_ORGS_SUCCESS:
      return {
        ...state,
        adminedOrganisations: action.payload.data.data,
      };

    default:
      return state;
  }
};

export const signin = params => dispatch => {
  return dispatch({
    types: [SIGNING_IN, SIGNIN_SUCCESS, SIGNIN_FAILED],
    payload: {
      request: {
        method: 'post',
        url: urls.sessionToken.create,
        data: {
          user: {
            email: params.email,
            password: params.password,
          },
        },
      },
    },
  });
};

export const refreshSessionToken = () => dispatch => {
  let promise = dispatch({
    types: [
      REFRESHING_SESSION_TOKEN,
      REFRESH_SESSION_TOKEN_SUCCESS,
      REFRESH_SESSION_TOKEN_FAILED,
    ],
    payload: {
      request: {
        method: 'post',
        url: urls.sessionToken.create,
        data: {
          user: {
            token: localStorage.getItem('currentUser.refreshToken'),
          },
        },
      },
    },
  });

  dispatch({
    type: STORE_REFRESHING_PROMISE,
    promise,
  });

  promise.catch(error => {
    if (checkIfErrorCodePresent(error.error, 'invalid_session_token')) {
      signout()(dispatch);
    }
  });

  return promise;
};

export const reloadUser = () => dispatch => {
  const userId = localStorage.getItem('currentUser.user_id');

  return dispatch({
    types: [RELOADING_CURRENT_USER, RELOAD_SUCCESS, RELOAD_FAILED],
    payload: {
      request: {
        method: 'get',
        url: urls.users.show(userId),
      },
    },
  });
};

export const signout = () => dispatch => {
  clearSession();

  dispatch({ type: SIGNOUT });
};

export const setDefaultHospital = (user, hospital) => dispatch => {
  return dispatch({
    types: [
      DEFAULTING_HOSPITAL,
      DEFAULT_HOSPITAL_SUCCESS,
      DEFAULT_HOSPITAL_FAILED,
    ],
    payload: {
      request: {
        method: 'patch',
        url: urls.users.update(user.id),
        data: {
          user: {
            hospital_id: hospital.id,
          },
        },
      },
    },
  });
};

export const updateUser = (params, opts = {}) => dispatch => {
  let onSuccessDispatch = ({ getState, response }) => {
    if (params.new_email) {
      dispatch({ type: USER_EMAIL_CHANGE_REQUESTED });
    }
  };

  let onErrorDispatch = ({ getState, error }) => {
    if (checkIfErrorCodePresent(error, 'invalid_current_password')) {
      dispatch({ type: INVALID_CURRENT_PASSWORD });
    }
  };

  const userParams = {
    current_password: params.current_password,
    first_name: params.first_name,
    last_name: params.last_name,
    new_email: params.new_email,
    password: params.password,
    password_confirmation: params.password_confirmation,
    pbrn_type: params.pbrn_type,
    professional_body_registration_number:
      params.professional_body_registration_number,
    rank: params.rank,
    role: params.role,
    grade: params.grade,
    validate_role_and_grade_presence: true,
  };

  const userId = localStorage.getItem('currentUser.user_id');

  let promise = dispatch({
    types: [UPDATING_USER, UPDATE_USER_SUCCESS, UPDATE_USER_FAILED],
    payload: {
      request: {
        method: 'patch',
        url: urls.users.update(userId),
        data: { user: userParams },
      },
    },
  });

  promise.then(onSuccessDispatch, onErrorDispatch);

  return promise;
};

export const fetchAdminedOrganisations = () => dispatch => {
  return dispatch({
    types: [ADMINED_ORGS, ADMINED_ORGS_SUCCESS, ADMINED_ORGS_FAILED],
    payload: {
      request: {
        method: 'get',
        url: urls.organisationsApi.organisations.adminedOrganisations.index,
      },
      customClient: ORGANISATIONS_API_CLIENT,
    },
  });
};

export const favouriteHospital = hospital => dispatch => {
  return dispatch({
    types: [
      FAVOURITING_HOSPITAL,
      FAVOURITE_HOSPITAL_SUCCESS,
      FAVOURITE_HOSPITAL_FAILED,
    ],
    payload: {
      request: {
        method: 'post',
        url: urls.favouriteHospitals.create(hospital.id),
      },
    },
    favouritingHospitalId: hospital.id,
    hospital: hospital,
  });
};

export const unfavouriteHospital = (hospital, opts = {}) => dispatch => {
  return dispatch({
    types: [
      UNFAVOURITING_HOSPITAL,
      UNFAVOURITE_HOSPITAL_SUCCESS,
      UNFAVOURITE_HOSPITAL_FAILED,
    ],
    payload: {
      request: {
        method: 'delete',
        url: urls.favouriteHospitals.destroy(hospital.id),
      },
    },
    unfavouritedHospitalId: hospital.id,
  });
};

export const hardDeleteCurrentUser = () => dispatch => {
  const userId = localStorage.getItem('currentUser.user_id');

  let promise = dispatch({
    types: [HARD_DELETING, HARD_DELETE_SUCCESS, HARD_DELETE_FAILED],
    payload: {
      request: {
        method: 'post',
        url: urls.users.hardDelete(userId),
      },
    },
  });

  promise.then(signout()(dispatch));

  return promise;
};

export const updateUserTealiumId = (
  tealium_visitor_id,
  opts = {}
) => dispatch => {
  const userParams = {
    tealium_visitor_id,
  };

  const userId = localStorage.getItem('currentUser.user_id');

  return dispatch({
    types: [
      UPDATING_USER_TEALIUM_ID,
      UPDATE_USER_TEALIUM_ID_SUCCESS,
      UPDATE_USER_TEALIUM_ID_FAILED,
    ],
    payload: {
      request: {
        method: 'patch',
        url: urls.users.update(userId),
        data: { user: userParams },
      },
    },
  });
};

export const updateUserGdprConsent = (userId, params) => dispatch => {
  return dispatch({
    types: [UPDATING_GDPR_CONSENT, UPDATE_GDPR_SUCCESS, UPDATE_GDPR_FAILED],
    payload: {
      request: {
        method: 'post',
        url: urls.users.v3.updateGdprConsent(userId),
        data: {
          tealium_event: params.tealium_event,
          policy: params.policy,
          tealium_visitor_id: params.tealium_visitor_id,
        },
      },
    },
  });
};
